import DOMPurify from "dompurify";
import React, { FC } from "react";
import BlockRenderer from "../BlockRenderer/BlockRenderer";
import { createPortal } from "react-dom";
import {
  checkChildrenExitenceInRecurrence,
  checkClassExistenceInRecurence,
  checkDataAttributeExistenceInRecurrence,
  cleanCSSPropertyInRecurence,
  preprocessHTMLForIframes,
  sanitizeOrPassthrough,
} from "../../helpers/rteHelpers";
import { SsrTagClassMap } from "../../helpers/rteSsrSanitizer";

const localPurifier = DOMPurify();

// Mirrors the classes the "beforeSanitizeAttributes" hook below applies per tag,
// so SSR markup matches what the client-side DOMPurify hook produces.
const RTE_SSR_TAG_CLASSES: SsrTagClassMap = {
  h2: "max-w-[836px] md:mx-auto mx-3 text-mob-article-h3 md:text-article-h3 mb-8 text-primary",
  h3: "max-w-[836px] md:mx-auto mx-3 text-mob-article-h3 md:text-article-h3 mb-8 text-primary",
  h4: "max-w-[836px] md:mx-auto mx-3 text-mob-article-h4 md:text-article-h4 mb-8 font-medium text-primary",
  h5: "max-w-[836px] md:mx-auto mx-3 text-lg mb-8 font-medium text-primary",
  h6: "max-w-[836px] md:mx-auto mx-3 text-base mb-8 font-medium text-primary",
  a: "text-primaryBlue underline font-semibold",
  blockquote: "max-w-[836px] md:mx-auto bg-[#EBF8EE] p-14 md:text-mob-article-subtitle text-mob-article-paragraph text-center",
  ul: "max-w-[836px] mx-3 md:mx-auto text-primary md:text-article-paragraph list-disc",
  ol: "max-w-[836px] mx-3 md:mx-auto text-primary md:text-article-paragraph list-decimal",
  li: "marker:text-primary text-primary md:text-article-paragraph",
  p: "md:max-w-[836px] mx-3 md:mx-auto md:text-article-paragraph text-primary mb-8",
  table: "text-primary",
  tr: "text-primary",
  em: "italic",
};

localPurifier.addHook("beforeSanitizeAttributes", (node: HTMLElement) => {
  if (node.style?.fontFamily) {
    node.style.removeProperty("font-family");
    node.style.removeProperty("font-size");
    node.style.removeProperty("background-color");
  }

  if (node.nodeName === "H2") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto mx-3 text-mob-article-h3 md:text-article-h3 mb-8 text-primary"
    );
  }

  if (node.nodeName === "H3") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto mx-3 text-mob-article-h3 md:text-article-h3 mb-8 text-primary"
    );
  }

  if (node.nodeName === "H4") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto mx-3 text-mob-article-h4 md:text-article-h4 mb-8 font-medium text-primary"
    );
  }

  if (node.nodeName === "H5") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto mx-3 text-lg mb-8 font-medium text-primary"
    );
  }

  if (node.nodeName === "H6") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto mx-3 text-base mb-8 font-medium text-primary"
    );
  }

  if (node.nodeName === "A") {
    node.style.removeProperty("color");
    node.setAttribute("class", "text-primaryBlue underline font-semibold");
  }

  if (
    node.nodeName === "IMG" ||
    checkChildrenExitenceInRecurrence(node, "IMG")
  ) {
    if (node.nodeName === "DIV") {
      node.classList.add("mb-8");
    }

    if (node.style?.float) {
      node.classList.remove("md:mx-auto");
      node.classList.remove("mx-3");
      node.classList.remove("max-w-[964px]");
      node.classList.remove("max-w-[836px]");
      node.parentElement?.classList.remove("max-w-[964px]");
    }

    if (node.style?.float === "left") {
      node.classList.add("mr-8");
      node.classList.add("mb-8");
    }

    if (node.style?.float === "right") {
      node.classList.add("ml-8");
      node.classList.add("mb-8");
    }

    if (
      node.classList.contains("wide-image") ||
      checkClassExistenceInRecurence(node, "wide-image")
    ) {
      node.classList.add("w-full");
    } else {
      node.classList.add("md:max-w-[964px]");
    }
  }

  if (node.nodeName === "IFRAME") {
    node.style.width = "100%";
    node.style.height = "100%";
    node.style.minHeight = "480px";
  }

  if (node.nodeName === "BLOCKQUOTE") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto bg-[#EBF8EE] p-14 md:text-mob-article-subtitle text-mob-article-paragraph text-center"
    );
    node.childNodes.forEach((child: HTMLElement) => {
      child.style?.removeProperty("text-align");
      cleanCSSPropertyInRecurence(child, "font-size");
    });
  }

  if (node.nodeName === "UL" || node.nodeName === "OL") {
    node.setAttribute(
      "class",
      "max-w-[836px] mx-3 md:mx-auto text-primary md:text-article-paragraph"
    );
  }

  if (node.nodeName === "UL") {
    node.classList?.add("list-disc");
  }

  if (node.nodeName === "OL") {
    node.classList?.add("list-decimal");
  }

  if (node.nodeName === "LI") {
    node.classList?.add("marker:text-primary");
    node.classList?.add("text-primary");
    node.classList?.add("md:text-article-paragraph");
    node.style.setProperty("display", "list-item");
    node.style.setProperty("margin-inline-start", "16px");
  }

  if (node.nodeName === "EM") {
    node.classList?.add("class", "italic");
  }
  if (node.nodeName === "P") {
    node.classList?.add("md:text-article-paragraph");
    node.classList?.add("text-primary");
    node.classList?.add("mb-8");
  }

  if (node.nodeName === "TABLE" || node.nodeName === "TR") {
    node.classList?.add("text-primary");
  }

  if (node.classList?.contains("img-flow")) {
    node.classList?.add("min-h-[670px]");
    node.classList?.remove("img-flow");
  }

  if (node.classList?.contains("quote")) {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto bg-[#EBF8EE] p-14 md:text-mob-article-subtitle text-mob-article-paragraph text-center"
    );
    node.childNodes.forEach((child: HTMLElement) => {
      child.style?.removeProperty("text-align");
      cleanCSSPropertyInRecurence(child, "font-size");
    });
  }

  if (node.classList?.contains("vcard")) {
    node.classList?.add("max-w-[836px]");
    node.classList?.add("md:mx-auto");
    node.classList?.add("p-0");
    node.classList?.add("static");
    node.classList?.remove("max-w-[964px]");
  }

  if (node.classList?.contains("vcard__photo")) {
    node.classList?.add("static");
    node.classList?.add("float-right");
  }
});

localPurifier.addHook("afterSanitizeElements", (node: HTMLElement) => {
  node.style?.removeProperty("margin");

  if (
    node.classList?.contains("wide-image") ||
    checkClassExistenceInRecurence(node, "wide-image")
  ) {
    node.classList?.add("mx-auto");
  } else if (
    !checkDataAttributeExistenceInRecurrence(node, "blockId") &&
    !checkChildrenExitenceInRecurrence(node, "IFRAME") &&
    node.nodeName !== "IFRAME"
  ) {
    node.classList?.add("md:max-w-[836px]");
    if (
      !node.classList?.contains("TextRun") &&
      !node.classList?.contains("NormalTextRun") &&
      !node.classList?.contains("Paragraph") &&
      node.nodeName !== "SPAN"
    ) {
      if (node.nodeName === "IMG") {
        node.classList?.add("md:mx-3");
        node.classList?.add("md:mx-auto");
      } else {
        node.classList?.add("mx-3");
        node.classList?.add("md:mx-auto");
      }
    } else {
      node.classList?.add("mx-auto");
    }
  } else {
    node.classList?.add("max-w-[1192px]");
    node.classList?.add("mx-auto");
    node.setAttribute("height", "670");
  }
});

const blockClassesOverrides = {
  TextAndImageBlock: {
    overridesForRightClasses:
      "w-full lg:w-1/2 justify-end p-8 md:p-16 flex flex-col whitespace-pre-wrap lg:pr-8",
    overridesForTextClasses: "w-full",
  },
};

interface RTEBlockProps {
  content: string;
}

const RTEBlock: FC<RTEBlockProps> = ({ content }) => {
  const htmlRef = React.useRef<HTMLDivElement>(null);
  const [nodes, setNodes] = React.useState<HTMLElement[]>([]);

  const memoizedContent = React.useMemo(() => {
    const preprocessedHTML = preprocessHTMLForIframes(content);

    return sanitizeOrPassthrough(localPurifier, preprocessedHTML, {
      options: {
        ALLOWED_TAGS: [
          "iframe",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "a",
          "img",
          "blockquote",
          "ul",
          "li",
          "ol",
          "em",
          "strong",
          "p",
          "span",
          "div",
          "br",
          "table",
          "tr",
          "th",
          "td",
          "script",
        ],
        ALLOWED_ATTR: [
          "src",
          "type",
          "async",
          "defer",
          "charset",
          "class",
          "href",
          "id",
          "style",
          "data-iframe-wrapped",
        ],
      },
      ssrTagClassMap: RTE_SSR_TAG_CLASSES,
    });
  }, [content]);

  React.useEffect(() => {
    if (!htmlRef.current) {
      return;
    }

    const nodes: HTMLElement[] = Array.from(
      htmlRef.current.querySelectorAll("[data-block-id]")
    );
    setNodes(nodes);

    const handleAnchorClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href?.startsWith("#")) return;

      const target = document.getElementById(href.slice(1));
      if (!target) return;

      e.preventDefault();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    htmlRef.current.addEventListener("click", handleAnchorClick);
    return () => {
      htmlRef.current?.removeEventListener("click", handleAnchorClick);
    };
  }, [htmlRef.current]);

  return (
    <>
      <div
        className=" mx-auto my-12"
        ref={htmlRef}
        dangerouslySetInnerHTML={{
          __html: memoizedContent,
        }}
      />
      {nodes.map((component) => {
        const contentId = component.getAttribute("data-block-id");
        const jsxElement = (
          <BlockRenderer
            contentId={contentId}
            overridesForClasses={blockClassesOverrides}
          />
        );

        return createPortal(jsxElement, component);
      })}
    </>
  );
};

export default RTEBlock;
